import { DOSSIERS_QUERY_KEY, invalidateOpenDossiersQueries, useUpdateDossierPropertyQueriesData } from "@/entities/dossier";
import { useAuth, UserRole } from "@/features/auth";
import { queryClient, useApiClient } from "@/infrastructure/rest";
import { ActieEvent, useRealtimeTopic } from "@/infrastructure/rsocket";
import { useNotificationPermissions, useSendNotifications } from "@/shared/hooks";
import { ActieDto } from "@/shared/types";
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { useEffect } from "react";
import { ACTIES_QUERY_KEY, ActieStatus } from "./constants.ts";
import { Actie } from "./domain";

function getDossierActiesQuery(id: string, apiClient: ReturnType<typeof useApiClient>) {
    return {
        queryKey: [DOSSIERS_QUERY_KEY, id, ACTIES_QUERY_KEY],
        queryFn: async () => {
            const data = await apiClient.get<ActieDto[]>(`/dossiers/${id}/acties`);
            return data?.map((dto) => Actie.fromDto(dto)) ?? [];
        },
    };
}

export function usePrefetchDossierActiesQuery(dossierId: string) {
    const apiClient = useApiClient();
    const query = getDossierActiesQuery(dossierId, apiClient);
    return () => queryClient.prefetchQuery({ ...query, staleTime: 60_000 });
}

export function useGetDossierActiesById<T = Actie[]>(
    id: string,
    options?: Omit<UseQueryOptions<Actie[], Error, T>, "queryFn" | "queryKey">
) {
    const apiClient = useApiClient();
    const query = getDossierActiesQuery(id, apiClient);

    return useQuery({
        ...query,
        ...options,
    });
}

export function useRealtimeActies() {
    const { hasRole } = useAuth();
    const [hasPermission, requestPermission] = useNotificationPermissions();
    const sendNotification = useSendNotifications();
    const { create, update, remove } = useUpdateDossierPropertyQueriesData("acties");

    useRealtimeTopic<ActieEvent>("acties", {
        onMessage: (message) => {
            const { actie, dossierId, reason } = message;
            const domainActie = Actie.fromDto(actie);
            switch (reason) {
                case "Nieuw":
                    create(dossierId, () => domainActie);
                    // trigger notification with same information as timeline content (if permission was given)
                    sendNotification(domainActie.timelineContent.titel, domainActie.timelineContent.beschrijving);
                    break;
                case "Wijziging":
                    if (actie.status !== ActieStatus.NIEUW) {
                        remove(dossierId, actie.id);
                        break;
                    }
                    // trigger notification with same information as timeline content (if permission was given)
                    sendNotification(domainActie.timelineContent.titel, domainActie.timelineContent.beschrijving);
                    update(dossierId, actie.id, () => domainActie);
                    break;
                case "Verwijderd":
                    remove(dossierId, actie.id);
                    break;
            }
        },
    });

    useEffect(() => {
        // Immediately ask for permissions for RCB users when we subscribe, to make sure they have them enabled
        if (!hasPermission && (hasRole(UserRole.DEWIT) || hasRole(UserRole.TRANSVISION))) {
            requestPermission();
        }
    }, [hasPermission, hasRole, requestPermission]);
}

type ProcessActieParams = {
    dossierId: string;
    actieId: number;
    toelichting?: string | null;
};

export function useAcceptActie() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, actieId, toelichting = null }: ProcessActieParams) => {
            await apiClient.patch(`/dossiers/${dossierId}/acties/${actieId}/status`, {
                status: ActieStatus.GEACCEPTEERD,
                toelichting,
            });
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error accepting actie:", error);
        },
    });
}

export function useDeclineActie() {
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierId, actieId, toelichting = null }: ProcessActieParams) => {
            try {
                await apiClient.patch(`/dossiers/${dossierId}/acties/${actieId}/status`, {
                    status: ActieStatus.GEWEIGERD,
                    toelichting,
                });
                return true;
            } catch (e) {
                console.error("Error declining actie:", e);
                return false;
            }
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error declining actie:", error);
        },
    });
}
type ReplaceActieParams = {
    actie: ActieDto;
    dossierId: string;
    actieId: number;
};

export function useReplaceActie() {
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierId, actieId, actie }: ReplaceActieParams) => {
            await apiClient.put(`/dossiers/${dossierId}/acties/${actieId}`, {
                ...actie,
                status: ActieStatus.GEACCEPTEERD,
                afgehandeldOpDatumTijd: new Date().toISOString(),
            });
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error replacing actie:", error);
        },
    });
}
